import { useMemo } from 'react';
import { LineChart, Line, ReferenceLine, YAxis } from 'recharts';
import { AlertTriangle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface VitalSparklineProps {
  readings: Array<{ value: number; isAlert?: boolean }>;
  min?: number | null;
  max?: number | null;
  unit?: string;
  currentValue: number;
  color?: string;
}

export function VitalSparkline({ readings, min, max, unit, currentValue, color = 'hsl(var(--primary))' }: VitalSparklineProps) {
  const { t } = useTranslation();

  const data = useMemo(() => readings.map((r, i) => ({ idx: i, value: r.value })), [readings]);

  const trend = useMemo(() => {
    if (readings.length < 3) return 'stable';
    const recent = readings.slice(-3);
    const diffs = recent.slice(1).map((r, i) => r.value - recent[i].value);
    const avgDiff = diffs.reduce((a, b) => a + b, 0) / diffs.length;
    if (avgDiff > 0.5) return 'up';
    if (avgDiff < -0.5) return 'down';
    return 'stable';
  }, [readings]);

  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;

  const isOutOfRange = (min != null && currentValue < min) || (max != null && currentValue > max);
  const rangeText = min != null && max != null
    ? `${min}–${max} ${unit || ''}`
    : min != null ? `≥ ${min} ${unit || ''}`
    : max != null ? `≤ ${max} ${unit || ''}` : null;

  if (readings.length < 2) return null;

  return (
    <div className="flex items-center gap-2">
      <div className="w-[80px] h-[28px]">
        <LineChart width={80} height={28} data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <YAxis hide domain={['dataMin - 2', 'dataMax + 2']} />
          {min != null && <ReferenceLine y={min} stroke="hsl(var(--warning))" strokeDasharray="2 2" strokeWidth={0.5} />}
          {max != null && <ReferenceLine y={max} stroke="hsl(var(--warning))" strokeDasharray="2 2" strokeWidth={0.5} />}
          <Line
            type="monotone"
            dataKey="value"
            stroke={isOutOfRange ? 'hsl(var(--destructive))' : color}
            strokeWidth={1.5}
            dot={false}
          />
        </LineChart>
      </div>
      <TrendIcon className={`w-3 h-3 ${isOutOfRange ? 'text-destructive' : 'text-muted-foreground'}`} />
      {rangeText && (
        <span className="text-[10px] text-muted-foreground whitespace-nowrap">
          {t('executeTask.normalRange')}: {rangeText}
        </span>
      )}
    </div>
  );
}
